import Image from 'next/image'
import Link from 'next/link'
import { useEffect } from 'react'
import { AnyObject, Form } from 'react-final-form'

import { SubmissionErrors } from 'final-form'
import { useConnect } from 'wagmi'

import { Button } from '@infura/packages.ui/Button'

import { LOGIN_METHOD_NAME } from 'lib/constants'
import { Infura } from 'lib/types/infuraTypes'

type Props = {
  onSubmit: (props: {
    authLoginMethod: Infura.AUTH_LOGIN_METHOD.WALLET
    recaptcha: string
    user: { email: string }
  }) => Promise<boolean | AnyObject | SubmissionErrors>
  onError?: (error: string) => void
}

type FormContentProps = {
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  submitError?: string
  submitting: boolean
  onError?: (error: string) => void
}

const FormContent = ({ handleSubmit, onError, submitError, submitting }: FormContentProps) => {
  useEffect(() => {
    if (submitError && onError) {
      onError(submitError)
    }
  }, [submitError, onError])

  return (
    <form className='xxl:text-lg w-full text-base' data-testid='auth-form' onSubmit={handleSubmit}>
      <Button
        data-testid='mm-auth-button'
        disabled={submitting}
        fullWidth
        id='mm-auth-button'
        loading={submitting}
        onClick={() => {
          if (window.analytics) {
            window.analytics.track('Login Initiated', {
              login_method: 'Metamask',
              timestamp: Date.now(),
              user_experience: 'B'
            })
          }
          sessionStorage.setItem(LOGIN_METHOD_NAME, 'Metamask')
        }}
        theme='secondary'
        type='submit'
      >
        <span className='flex items-center justify-center gap-2'>
          <Image
            alt='MetaMask icon'
            className='mr-1.5 h-4 w-4'
            height='16'
            src='/svg/icons/PrimaryMetaMask.svg'
            width='16'
          />
          Continue with MetaMask
        </span>
      </Button>
    </form>
  )
}

const WalletLoginForm = ({ onError, onSubmit }: Props) => {
  const { connectors } = useConnect()
  const connector = connectors.find(c => ['MetaMask Flask', 'MetaMask'].includes(c.name))

  if (!connector)
    return (
      <Link
        className='hover:shadow-hover border-border-muted text-text-alternative inline-flex w-full items-center justify-center border py-3 no-underline hover:bg-white hover:no-underline'
        href='https://metamask.io/download/'
        target='_blank'
      >
        <Image alt='MetaMask icon' className='mr-1.5 h-4 w-4' height='16' src='/svg/icons/MetaMask.svg' width='16' />
        <span className='text-sm uppercase tracking-widest'>Install MetaMask</span>
      </Link>
    )

  return (
    <Form
      initialValues={{ authLoginMethod: Infura.AUTH_LOGIN_METHOD.WALLET }}
      onSubmit={onSubmit}
      render={({ handleSubmit, submitError, submitting }) => (
        <FormContent handleSubmit={handleSubmit} onError={onError} submitError={submitError} submitting={submitting} />
      )}
    />
  )
}

export default WalletLoginForm
